import { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import { Typography, Box, Button, CircularProgress } from "@mui/material";
import { Save } from "@mui/icons-material";

import { AppPage, AppHeader, AppCard } from "../../../layout/layout";
import { useApiError } from "../../../hooks/useApiError";
import { recipeService } from "../../../services/recipe-service";
import { dropdownService } from "../../../services/dropdown-service";

import RecipeIngredientsSection from "./_components/RecipeIngredientsSection";
import RecipeMetaSection, { DropdownVm } from "./_components/RecipeMetaSection";
import RecipeInstructionsSection from "./_components/RecipeInstructionSection";
import { useNavigate, useParams } from "react-router-dom";
import RecipeImageSection from "./_components/RecipeImageSection";

type IngredientRow = {
    id: number;
    text: string;
};

export default function RecipeManage() {
    const navigate = useNavigate();
    const { id } = useParams();

    const recipeId = useMemo(() => {
        const n = Number(id);
        return Number.isFinite(n) && n > 0 ? n : null;
    }, [id]);

    const isEdit = recipeId !== null;

    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");

    const [categories, setCategories] = useState<DropdownVm[]>([]);
    const [categoriesSelected, setCategoriesSelected] = useState<DropdownVm[]>([]);
    const [loadingCategories, setLoadingCategories] = useState(false);

    const [ingredients, setIngredients] = useState<IngredientRow[]>([{ id: 1, text: "" }]);
    const [instructionsHtml, setInstructionsHtml] = useState("");

    const [saving, setSaving] = useState(false);
    const [loadingRecipe, setLoadingRecipe] = useState(false);

    const { handleError, ErrorAlert, clearError } = useApiError();

    const resetForm = () => {
        setTitle("");
        setDescription("");
        setIngredients([{ id: 1, text: "" }]);
        setInstructionsHtml("");
        setCategoriesSelected([]);
    };

    // Load categories (always)
    useEffect(() => {
        const loadCats = async () => {
            setLoadingCategories(true);
            try {
                const data = await dropdownService.getAll<DropdownVm>("categories");
                setCategories(data);
            } catch (err) {
                handleError(err, "Failed to load categories.");
            } finally {
                setLoadingCategories(false);
            }
        };

        loadCats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load recipe when editing
    useEffect(() => {
        if (!isEdit) return;

        const loadRecipe = async () => {
            setLoadingRecipe(true);
            try {
                const dto = await recipeService.getById(recipeId!);

                setTitle(dto.title ?? "");
                setDescription(dto.description ?? "");
                setInstructionsHtml(dto.instructionsHtml ?? "");

                setIngredients(
                    (dto.ingredients ?? []).length
                        ? dto.ingredients.map((t, idx) => ({ id: idx + 1, text: t }))
                        : [{ id: 1, text: "" }]
                );

                // categoriesSelected will be set after categories are loaded (below)
            } catch (err) {
                handleError(err, "Failed to load recipe.");
            } finally {
                setLoadingRecipe(false);
            }
        };

        loadRecipe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit, recipeId]);

    // Once both recipe + categories exist, set selected categories (edit mode)
    useEffect(() => {
        if (!isEdit) return;
        if (categories.length === 0) return;

        // We need the recipe's categoryIds. If your getById doesn't return categoryIds yet, this won't work.
        const applySelected = async () => {
            try {
                const dto = await recipeService.getById(recipeId!);
                const selected = categories.filter((c) => (dto.categoryIds ?? []).includes(c.id));
                setCategoriesSelected(selected);
            } catch {
                // ignore; loadRecipe effect already handles errors
            }
        };

        applySelected();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categories, isEdit, recipeId]);

    const handleAddIngredientRow = () => {
        clearError();
        setIngredients((prev) => [
            ...prev,
            {
                id: prev.length === 0 ? 1 : Math.max(...prev.map((i) => i.id)) + 1,
                text: "",
            },
        ]);
    };

    function handleIngredientChange(id: number, value: string) {
        clearError();
        setIngredients((prev) => prev.map((i) => (i.id === id ? { ...i, text: value } : i)));
    }

    function handleRemoveIngredientRow(id: number) {
        clearError();
        setIngredients((prev) => prev.filter((i) => i.id !== id));
    }

    async function handleSave() {
        clearError();

        if (!title.trim()) {
            handleError(null, "Title is required.");
            return;
        }

        if (categoriesSelected.length === 0) {
            handleError(null, "Please select at least one category.");
            return;
        }

        const filteredIngredients = ingredients
            .map((i) => ({ ...i, text: i.text.trim() }))
            .filter((i) => i.text);

        if (filteredIngredients.length === 0) {
            handleError(null, "Please add at least one ingredient.");
            return;
        }

        setSaving(true);
        try {
            const payload = {
                title: title.trim(),
                description: description.trim() || null,
                categoryIds: categoriesSelected.map((c) => c.id),
                ingredients: filteredIngredients.map((i) => i.text),
                instructionsHtml: instructionsHtml || "",
            };

            if (isEdit) {
                await recipeService.update({ id: recipeId!, ...payload });
            } else {
                await recipeService.create(payload);
                resetForm();
            }

            navigate("/admin/recipes");
        } catch (err) {
            handleError(err, isEdit ? "Failed to update recipe." : "Failed to save recipe.");
        } finally {
            setSaving(false);
        }
    }

    return (
        <AppPage>
            <AppHeader
                title={isEdit ? "Edit recipe" : "Create recipe"}
                subtitle={
                    isEdit
                        ? "Update your recipe details."
                        : "Write a new recipe with ingredients and detailed cooking instructions."
                }
            />

            <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <AppCard>
                        <ErrorAlert />

                        {(loadingRecipe || loadingCategories) && isEdit ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                <RecipeMetaSection
                                    title={title}
                                    description={description}
                                    categoriesSelected={categoriesSelected}
                                    categories={categories}
                                    loadingCategories={loadingCategories}
                                    onTitleChange={setTitle}
                                    onDescriptionChange={setDescription}
                                    onCategoriesChange={(vals) => {
                                        clearError();
                                        setCategoriesSelected(vals);
                                    }}
                                />

                                <Box sx={{ mt: 3 }}>
                                    <RecipeIngredientsSection
                                        ingredients={ingredients}
                                        onAddRow={handleAddIngredientRow}
                                        onChangeRow={handleIngredientChange}
                                        onRemoveRow={handleRemoveIngredientRow}
                                    />
                                </Box>

                                <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 1 }}>
                                    <Button
                                        startIcon={<Save />}
                                        variant="contained"
                                        onClick={handleSave}
                                        disabled={saving || loadingCategories || loadingRecipe}
                                    >
                                        {saving ? "Saving..." : isEdit ? "Update recipe" : "Save recipe"}
                                    </Button>
                                </Box>
                            </>
                        )}
                    </AppCard>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <AppCard>
                        <RecipeInstructionsSection value={instructionsHtml} onChange={setInstructionsHtml} />
                    </AppCard>

                    <Box sx={{ mt: 3 }}>
                        <AppCard>
                            <RecipeImageSection recipeId={recipeId} isEdit={isEdit} />
                        </AppCard>
                    </Box>
                </Grid>

            </Grid>
        </AppPage>
    );
}
